'use client';

import { useEffect, useRef, useState } from 'react';

import {
  isCcsdCodeSearchPending,
  resolveUnifiedSearch,
  type UnifiedSearchResult,
} from '@/features/search';

import type {
  AutocompleteSuggestion,
  DoctorSuggestion,
  SpecialitySuggestion,
} from './types';

/** Maps the unified pipeline result onto flat autocomplete rows. */
export function mapUnifiedResultToSuggestions(
  result: UnifiedSearchResult,
): AutocompleteSuggestion[] {
  // 1. Specialities from the mapping scope (matched + related rows)
  const specialitySuggestions: SpecialitySuggestion[] = result.specialities.map(
    (entry) => ({
      type: 'speciality',
      slug: entry.path,
      title: entry.pageName,
      url: entry.path,
    }),
  );

  // 2. Specialists: name matches OR within the speciality scope
  const doctorSuggestions: DoctorSuggestion[] = result.doctors.map((doc) => ({
    type: 'doctor',
    slug: doc.slug,
    fullName: doc.fullName ?? doc.slug,
    heading: doc.heading ?? undefined,
    primarySpeciality: doc.primarySpeciality ?? undefined,
    specialities: doc.specialities ?? [],
    url: `/our-specialists/${doc.slug}`,
  }));

  // Combine: specialities, then doctors. (CCSD matches are not
  // shown as rows — they only scope the consultants below.)
  return [...specialitySuggestions, ...doctorSuggestions];
}

interface UseAutocompleteSearchOptions {
  searchTerm: string;
  selectedSpecialities: string[];
}

/**
 * Debounced autocomplete pipeline: classifies the query (speciality /
 * symptom / CCSD code / specialist name), resolves the speciality scope,
 * then derives specialists through that scope only.
 * Guards against stale requests and unmounted updates.
 */
export function useAutocompleteSearch({
  searchTerm,
  selectedSpecialities,
}: UseAutocompleteSearchOptions) {
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const trimmed = searchTerm.trim();
      if (trimmed.length < 2) {
        if (isMountedRef.current) {
          setSuggestions([]);
          setIsLoading(false);
        }
        return;
      }
      // CCSD code search only begins after 3 characters: clear results and
      // skip the pipeline entirely for a short digit-containing prefix
      // like "W3" so no dropdown activity happens yet.
      if (isCcsdCodeSearchPending(trimmed)) {
        if (isMountedRef.current) {
          setSuggestions([]);
          setIsLoading(false);
        }
        return;
      }
      const currentId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const result: UnifiedSearchResult = await resolveUnifiedSearch(
          trimmed,
          { specialitySlugs: selectedSpecialities },
        );

        // Guard against stale requests AND unmounted component
        if (currentId !== requestIdRef.current || !isMountedRef.current) return;

        if (isMountedRef.current)
          setSuggestions(mapUnifiedResultToSuggestions(result));
      } catch (err) {
        if (isMountedRef.current) {
          console.error('Failed to fetch autocomplete suggestions:', err);
        }
      } finally {
        if (currentId === requestIdRef.current && isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    const timer = setTimeout(fetchSuggestions, 150);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedSpecialities]);

  return { suggestions, isLoading };
}
